import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, Sparkles, Calendar, MapPin, Clock, CreditCard,
  CheckCircle, AlertCircle, XCircle, Loader2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: Clock },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700', icon: Clock },
  pending_payment: { label: 'Pending Payment', color: 'bg-blue-100 text-blue-700', icon: CreditCard },
  confirmed: { label: 'Confirmed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  active: { label: 'Active', color: 'bg-purple-100 text-purple-700', icon: Sparkles },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const paymentStatusConfig = {
  unpaid: { label: 'Unpaid', color: 'bg-slate-100 text-slate-700' },
  pending: { label: 'Processing', color: 'bg-amber-100 text-amber-700' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
  refunded: { label: 'Refunded', color: 'bg-blue-100 text-blue-700' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700' }
};

const AmbassadorBookingDetails = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [paying, setPaying] = useState(false);
  const [pollingPayment, setPollingPayment] = useState(false);

  // Check for payment status from URL params (return from Stripe)
  const sessionId = searchParams.get('session_id');
  const paymentUrlStatus = searchParams.get('payment');

  const fetchBooking = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/booking/${bookingId}`);
      setBooking(response.data);
    } catch (error) {
      console.error('Failed to load booking:', error);
      toast.error('Failed to load booking details');
      navigate('/client/ambassador-bookings');
    } finally {
      setLoading(false);
    }
  }, [bookingId, navigate]);

  // Poll payment status if returning from Stripe
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPollingPayment(false);
      toast.info('Payment status check timed out. Please check your email for confirmation.');
      fetchBooking();
      return;
    }

    try {
      const response = await api.get(`/ambassador-checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPollingPayment(false);
        toast.success('Payment successful! Your booking is confirmed.');
        // Clear URL params and refresh booking
        navigate(`/client/ambassador-booking/${bookingId}`, { replace: true });
        fetchBooking();
        return;
      } else if (response.data.status === 'expired') {
        setPollingPayment(false);
        toast.error('Payment session expired. Please try again.');
        navigate(`/client/ambassador-booking/${bookingId}`, { replace: true });
        fetchBooking();
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPollingPayment(false);
      fetchBooking();
    }
  }, [bookingId, navigate, fetchBooking]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  useEffect(() => {
    // If returning from successful payment, poll for status
    if (sessionId && paymentUrlStatus === 'success') {
      setPollingPayment(true);
      pollPaymentStatus(sessionId);
    } else if (paymentUrlStatus === 'cancelled') {
      toast.info('Payment cancelled');
      navigate(`/client/ambassador-booking/${bookingId}`, { replace: true });
    }
  }, [sessionId, paymentUrlStatus, bookingId, navigate, pollPaymentStatus]);

  const handlePayNow = async () => {
    setPaying(true);
    try {
      const response = await api.post('/ambassador-checkout/create-session', {
        booking_id: bookingId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setPaying(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    
    setCancelling(true);
    try {
      await api.post(`/booking/${bookingId}/cancel`);
      toast.success('Booking cancelled');
      fetchBooking();
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!booking) return null;

  const status = statusConfig[booking.status] || statusConfig.draft;
  const paymentStatus = paymentStatusConfig[booking.payment_status] || paymentStatusConfig.unpaid;
  const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="booking-details-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/client/ambassador-bookings')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Bookings
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Booking Header */}
        <Card className="bg-white mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <img
                  src={booking.ambassador.avatar_url}
                  alt={booking.ambassador.name}
                  className="w-16 h-16 rounded-full"
                />
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-2xl font-bold">{booking.ambassador.name}</h1>
                    <Badge className={status.color}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {status.label}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground">
                    Booking #{booking.booking_number}
                  </p>
                </div>
              </div>
              
              <div className="text-right">
                <p className="text-3xl font-bold">${booking.pricing.total_price.toLocaleString()}</p>
                <Badge className={paymentStatus.color}>{paymentStatus.label}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Actions */}
        {booking.status === 'pending_approval' && (
          <Card className="bg-amber-50 border-amber-200 mb-6">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">Awaiting Admin Approval</p>
                <p className="text-sm text-amber-700">Your booking is being reviewed. You'll be notified once approved.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {booking.status === 'pending_payment' && (
          <Card className="bg-blue-50 border-blue-200 mb-6" data-testid="payment-required-card">
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-800">Payment Required</p>
                  <p className="text-sm text-blue-700">Complete payment to confirm your booking.</p>
                </div>
              </div>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handlePayNow}
                disabled={paying}
                data-testid="pay-now-btn"
              >
                {paying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  <>
                    Pay Now
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Polling payment status indicator */}
        {pollingPayment && (
          <Card className="bg-purple-50 border-purple-200 mb-6">
            <CardContent className="p-4 flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
              <div>
                <p className="font-medium text-purple-800">Verifying Payment</p>
                <p className="text-sm text-purple-700">Please wait while we confirm your payment...</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Success */}
        {booking.payment_status === 'paid' && booking.status === 'confirmed' && (
          <Card className="bg-green-50 border-green-200 mb-6">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Payment Confirmed</p>
                <p className="text-sm text-green-700">Your booking is confirmed and ready to go!</p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Booking Details */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle>Booking Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">
                    {booking.start_date}
                    {booking.end_date && booking.end_date !== booking.start_date && (
                      <> — {booking.end_date}</>
                    )}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Usage Scope</p>
                  <p className="font-medium capitalize">{booking.usage_scope?.replace(/_/g, ' ')}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Deployment</p>
                  <p className="font-medium capitalize">{booking.deployment_type?.replace(/_/g, ' ')}</p>
                </div>
              </div>
              
              {booking.instances_count > 1 && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Instances</p>
                  <p className="font-medium">{booking.instances_count} locations/screens</p>
                </div>
              )}
              
              {booking.is_retainer && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Monthly Retainer</p>
                  <p className="font-medium">
                    ${booking.monthly_retainer_price?.toLocaleString()}/month for {booking.retainer_months} months
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Price Breakdown */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle>Price Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Base Activation ({booking.pricing.tier})</span>
                  <span>${booking.pricing.base_price.toLocaleString()}</span>
                </div>
                
                {booking.addons?.length > 0 && booking.addons.map((addon) => (
                  <div key={addon.code} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {addon.name} {addon.quantity > 1 ? `x${addon.quantity}` : ''}
                    </span>
                    <span>${addon.total_price.toLocaleString()}</span>
                  </div>
                ))}
                
                {booking.pricing.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-${booking.pricing.discount_amount.toLocaleString()}</span>
                  </div>
                )}
                
                <div className="border-t pt-3 flex justify-between font-bold">
                  <span>Total</span>
                  <span>${booking.pricing.total_price.toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Event/Campaign Details */}
        {(booking.event_name || booking.campaign_name) && (
          <Card className="bg-white mt-6">
            <CardHeader>
              <CardTitle>{booking.event_name ? 'Event Details' : 'Campaign Details'}</CardTitle>
            </CardHeader>
            <CardContent>
              {booking.event_name && (
                <div className="space-y-2">
                  <p><strong>Event:</strong> {booking.event_name}</p>
                  {booking.event_location && <p><strong>Location:</strong> {booking.event_location}</p>}
                </div>
              )}
              {booking.campaign_name && (
                <div className="space-y-2">
                  <p><strong>Campaign:</strong> {booking.campaign_name}</p>
                  {booking.campaign_objectives && <p><strong>Objectives:</strong> {booking.campaign_objectives}</p>}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {!['completed', 'cancelled'].includes(booking.status) && (
          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={cancelling}
              className="text-red-600 hover:text-red-700"
            >
              {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Cancel Booking
            </Button>
            <Button onClick={() => navigate(`/ambassador/${booking.ambassador.slug}`)}>
              View Ambassador Profile
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AmbassadorBookingDetails;
